import { useRef } from 'react';

import type { TriggerOrderType } from '@/constants/advancedOrder';

import { fontStyles } from '@/styles/typography';

import { InlineAlertBox } from '../InlineAlertBox';

interface TokenPriceAlertProps {
  mode: 'buy' | 'sell' | TriggerOrderType;
  difference: number;
}

export function TokenPriceAlert({ mode, difference }: TokenPriceAlertProps) {
  const percentageFormatter = useRef(
    Intl.NumberFormat('en-US', {
      style: 'percent',
      maximumFractionDigits: 1,
      signDisplay: 'always', // always show the sign
    }),
  );

  const percentage = percentageFormatter.current.format(difference);
  let titleText: string;
  let descriptionText: string;

  switch (mode) {
    case 'stop-loss':
    case 'take-profit': {
      titleText =
        mode === 'stop-loss'
          ? 'Order blocked: trigger is at or above market'
          : 'Order blocked: trigger is at or below market';
      descriptionText =
        mode === 'stop-loss'
          ? 'This stop-loss would fire immediately. Set a trigger below the current market price.'
          : 'This take-profit would fire immediately. Set a trigger above the current market price.';
      break;
    }
    case 'buy':
    case 'sell': {
      titleText =
        mode === 'buy' ? 'Order blocked: price above market' : 'Order blocked: price below market';

      descriptionText = `This limit order can't be placed because the price is ${percentage} ${
        mode === 'buy' ? 'above' : 'below'
      } the current market price, so it would execute immediately at a loss. Set a limit price ${
        mode === 'buy' ? 'below' : 'above'
      } the market rate, or click 'Reset' to use the market rate.`;
      break;
    }
    default: {
      const exhaustiveMode: never = mode;
      throw new Error(`Unknown token price alert mode: ${exhaustiveMode}`);
    }
  }

  return (
    <InlineAlertBox
      title={
        <div className="flex w-full items-center justify-between">
          <span>{titleText}</span>{' '}
          <span
            className={fontStyles({
              variant: 'BODY_MD',
              class: 'text-token-red-900 self-center',
            })}
          >
            {percentage}
          </span>
        </div>
      }
      description={descriptionText}
    />
  );
}
